import axios from 'axios'
import { toast } from '@/utils/toast'
import { endSession } from '@/utils/session'

const BASE_API = import.meta.env.VITE_APP_BASE_API || '/cfes/fodhandler/admintool'
const service = axios.create({
  baseURL: BASE_API,
  timeout: 20000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json;charset=utf-8' },
})

service.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
)

service.interceptors.response.use(
  (response) => {
    // Check if response indicates SSO session expired
    const data = response.data

    // console.debug('[request interceptor] response data', data)
    if (data && (data.code === 401 || data.code === 'UNAUTHORIZED' || data.code === 'TOKEN_EXPIRED')) {
      console.warn('[request] SSO session expired, redirecting to login')
      endSession({ toastMessage: 'Session expired. Redirecting to login...' })
      return Promise.reject(new Error('Session expired'))
    } else if (data.code === 403) {
      toast.error('Insufficient permissions')
    } else if (data.code && data.code !== 200 && data.message) {
      toast.error(data.message)
    }
    return data ?? {}
  },
  (error) => {
    console.error('[request] error', error)
    const status = error.response?.status
    const errorMessage = error.response?.data?.message || error.message
    
    // Handle 401 Unauthorized or 403 Forbidden - SSO token expired
    if (status === 401 || status === 403) {
      console.warn('[request] SSO token expired (HTTP ' + status + '), redirecting to login')
      endSession({ toastMessage: 'Authentication expired. Redirecting to login...' })
      return Promise.reject(error)
    }
    
    // Handle other HTTP errors
    if (status >= 500) {
      toast.error(`Server error: ${errorMessage || 'Please try again later'}`)
    } else if (status >= 400) {
      toast.error(errorMessage || 'Request failed')
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please try again.')
    } else {
      toast.error(errorMessage || 'Network error. Please check your connection.')
    }
    
    return Promise.reject(error)
  }
)

// Unified request entry
export default async function request({ url, method = 'get', data, params, responseType, headers, baseURL, timeout }) {
  return service({ url, method, data, params, responseType, headers, baseURL, timeout })
}
